import { UserType } from '../enums';

const STEP_PATH_OVERRIDES = {
  KYC: '/perform-kyc',
  PerformKYC: '/perform-kyc',
  PerformKyc: '/perform-kyc',
  AcceptLegalAgreements: '/accept-legal-agreements',
  ApproveTermsAndConditions: '/accept-legal-agreements',
  TermsAndConditions: '/accept-legal-agreements',
};

export const stepToPath = (stepName) => {
  if (!stepName) return null;
  if (STEP_PATH_OVERRIDES[stepName]) return STEP_PATH_OVERRIDES[stepName];
  return `/${stepName
    .replace(/([A-Z])/g, '-$1')
    .toLowerCase()
    .replace(/^-/, '')}`;
};

export const getOnboardingStepPath = (currentStep) => {
  if (!currentStep) return null;
  return stepToPath(currentStep);
};

export const isKycOrLaterOnboardingStep = (currentStep) => {
  const normalizedStep = String(currentStep ?? '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();
  return ['kyc', 'performkyc', 'onboarded'].includes(normalizedStep);
};

export const getOnboardingStepPaths = (steps) => {
  if (!Array.isArray(steps) || steps.length === 0) return [];
  return steps.map((s) => (typeof s === 'string' ? stepToPath(s) : stepToPath(s?.step))).filter(Boolean);
};

export function isCollectDataStepCompleted(steps) {
  if (!Array.isArray(steps)) return false;
  return steps.some(
    (step) =>
      typeof step === 'object' &&
      step !== null &&
      (step?.step === 'CollectData' || step?.Step === 'CollectData') &&
      step?.isCompleted === true,
  );
}

export function isCompanyPersonDataStepCompleted(steps) {
  if (!Array.isArray(steps)) return false;
  return steps.some(
    (step) =>
      typeof step === 'object' &&
      step !== null &&
      (step?.step === 'CollectCompanyPersonData' || step?.Step === 'CollectCompanyPersonData') &&
      step?.isCompleted === true,
  );
}

function insertPathAfter(paths, afterPath, pathToInsert) {
  if (paths.includes(pathToInsert)) return paths;
  const afterIndex = paths.indexOf(afterPath);
  if (afterIndex < 0) return paths;
  return [...paths.slice(0, afterIndex + 1), pathToInsert, ...paths.slice(afterIndex + 1)];
}

function insertFinancialPath(paths, shouldIncludeFinancialStep, afterPath = '/collect-data') {
  if (!shouldIncludeFinancialStep) return paths;
  return insertPathAfter(paths, afterPath, '/financial');
}

function insertCompliancePath(paths, shouldIncludeComplianceStep, afterPath = '/financial') {
  if (!shouldIncludeComplianceStep) return paths;

  if (paths.includes('/compliance')) return paths;

  if (paths.includes(afterPath)) {
    return insertPathAfter(paths, afterPath, '/compliance');
  }

  return insertPathAfter(paths, '/collect-data', '/compliance');
}

export function isIndividualOnboardingUser(userOrType) {
  const userType = typeof userOrType === 'string' ? userOrType : userOrType?.userType;
  return userType === UserType.Individual || userType === 'Individual';
}

export function isCompanyOnboardingUser(userOrType) {
  const userType = typeof userOrType === 'string' ? userOrType : userOrType?.userType;
  return userType === UserType.Company || userType === 'Company';
}

export function getResolvedOnboardingStepPaths(steps, { userType } = {}) {
  const basePaths = getOnboardingStepPaths(steps);

  if (isIndividualOnboardingUser(userType) && isCollectDataStepCompleted(steps)) {
    return insertCompliancePath(insertFinancialPath(basePaths, true, '/collect-data'), true, '/financial');
  }

  if (isCompanyOnboardingUser(userType) && isCompanyPersonDataStepCompleted(steps)) {
    return insertCompliancePath(insertFinancialPath(basePaths, true, '/collect-company-person-data'), true, '/financial');
  }

  return basePaths;
}

export function getResolvedNextStepPath(currentPath, steps, { userType } = {}) {
  const paths = getResolvedOnboardingStepPaths(steps, { userType });
  const index = paths.indexOf(currentPath);
  if (index < 0 || index >= paths.length - 1) return null;
  return paths[index + 1];
}

export function getResolvedPreviousStepPath(currentPath, steps, { userType } = {}) {
  const paths = getResolvedOnboardingStepPaths(steps, { userType });
  const index = paths.indexOf(currentPath);
  if (index <= 0) return null;
  return paths[index - 1];
}

export const getPreviousStepPath = (currentPath, steps) => {
  const paths = getOnboardingStepPaths(steps || []);
  const index = paths.indexOf(currentPath);
  if (index <= 0) return null;
  return paths[index - 1];
};

export const getNextStepPath = (currentPath, steps) => {
  const paths = getOnboardingStepPaths(steps || []);
  const index = paths.indexOf(currentPath);
  if (index < 0 || index >= paths.length - 1) return null;
  return paths[index + 1];
};

/** Get the step name for a given path from the steps array (e.g. '/choose-user-type' -> 'ChooseUserType'). */
export const getStepFromPath = (currentPath, steps) => {
  if (!currentPath || !Array.isArray(steps) || steps.length === 0) return null;
  const paths = getOnboardingStepPaths(steps);
  const index = paths.indexOf(currentPath);
  if (index < 0) return null;
  const step = steps[index];
  return typeof step === 'string' ? step : (step?.step ?? null);
};

export function getPostCollectDataNextPath(steps, { userType } = {}) {
  if (isIndividualOnboardingUser(userType)) {
    return '/financial';
  }

  return getResolvedNextStepPath('/collect-data', steps, { userType }) ?? getNextStepPath('/collect-data', steps);
}

export function getPostCompanyPersonDataNextPath(steps, { userType } = {}) {
  if (isCompanyOnboardingUser(userType)) {
    return '/financial';
  }

  return getResolvedNextStepPath('/collect-company-person-data', steps, { userType }) ?? getNextStepPath('/collect-company-person-data', steps);
}

export function scrollOnboardingToTop() {
  if (typeof window === 'undefined') return;

  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}
